import { ServiceTypeEnum, StatementStatusEnum } from '@sr-sdks/api-sdk-axios';
import {
  type InvoicesQueryParams,
  paidInvoicesParams,
} from '../../../../api/payments/invoiceApi';
import type { StatementsQueryParams } from '../../../../api/payments/statementApi';
import { isRoyaltyServiceType } from '../../../../utils/helpers/common';
import { ensureStableSort } from '../../../../utils/helpers/sort';

/**
 * Statement-type filter tabs shared by the Title Summary and Client Summary
 * Statements sections, so the two pages can't drift on tab order or on which
 * services are hidden.
 */
export interface FilterTab {
  /**
   * The tab's identity: its `Tabs` value, and its `SERVICE_BADGE_PRESETS` key
   * for the visible label and icon.
   */
  service: ServiceTypeEnum;
  /**
   * Every service type this tab covers — what the table queries, and what tab
   * visibility is checked against (the title's services on Title Summary; the
   * services of the client's titles, and the client's listed records, on
   * Client Summary). Usually just `service` itself.
   * The exception is Disbursements, which covers both `disbursements` (DAS)
   * and `collectionAccount` (CAM) in a single tab, matching the sidebar
   * Disbursements page (`pages/v3/Payments/Disbursements.tsx`) and
   * `docs/payments/terminology-and-data-mapping.md`.
   */
  serviceTypes: ServiceTypeEnum[];
}

/**
 * Every statement-type filter tab, in Figma-pinned display order. Each tab's
 * visible text and icon are read from `SERVICE_BADGE_PRESETS`; only the order
 * is pinned here.
 */
const FILTER_TABS: FilterTab[] = [
  {
    service: ServiceTypeEnum.RoyaltiesAnz,
    serviceTypes: [ServiceTypeEnum.RoyaltiesAnz],
  },
  {
    service: ServiceTypeEnum.RoyaltiesWorld,
    serviceTypes: [ServiceTypeEnum.RoyaltiesWorld],
  },
  {
    service: ServiceTypeEnum.Disbursements,
    serviceTypes: [
      ServiceTypeEnum.Disbursements,
      ServiceTypeEnum.CollectionAccount,
    ],
  },
  {
    service: ServiceTypeEnum.Residuals,
    serviceTypes: [ServiceTypeEnum.Residuals],
  },
];

/**
 * Tabs suppressed on every page.
 *
 * Royalties World has no statement data and won't have any for some time —
 * APP-2090 delivers the royalty tables — so its tab would only ever open on an
 * empty table. {@link FILTER_TABS} deliberately keeps the full definition
 * above: bringing the tab back is deleting the entry here, nothing more.
 */
const HIDDEN_SERVICES: ServiceTypeEnum[] = [ServiceTypeEnum.RoyaltiesWorld];

/** {@link FILTER_TABS} minus anything in {@link HIDDEN_SERVICES}. */
export const VISIBLE_TABS = FILTER_TABS.filter(
  ({ service }) => !HIDDEN_SERVICES.includes(service),
);

/**
 * Whether a tab's `serviceTypes` are listed from the Statements API rather
 * than the Invoices API: only royalties have statement records (APP-2090), so
 * a set made up entirely of Royalties ANZ/World uses the Statements API and
 * anything else the Invoices API.
 */
export const usesStatementsApi = (serviceTypes?: ServiceTypeEnum[]) =>
  !!serviceTypes?.length && serviceTypes.every(isRoyaltyServiceType);

/**
 * The status a statement needs to be listed under a tab. Invoices are listed
 * when paid, which {@link paidInvoicesParams} sets.
 */
export const LISTED_STATEMENT_STATUS = StatementStatusEnum.Finalised;

/** The date field a tab's table sorts on by default, per API. */
export const dateSortKey = (isStatementsMode: boolean) =>
  isStatementsMode ? 'statementDate' : 'invoiceDate';

/** A tab's default sort: newest first. */
export const defaultTabSortBy = (isStatementsMode: boolean) =>
  `${dateSortKey(isStatementsMode)}:desc`;

/** What a tab's table is scoped to. */
export interface TabListScope {
  clientId?: string;
  /** Statements API only: the Invoices API has no serial-level filter. */
  serialTitleId?: string;
  serviceTypes?: ServiceTypeEnum[];
  titleId?: string;
  workspaceId?: string;
}

/** Which page of a tab's table to list. */
export interface TabListPage {
  page: number;
  searchTerm: string;
  sortBy: string;
}

/**
 * The page a tab's table opens on when the URL carries no page, search or
 * sort.
 */
export const firstTabListPage = (isStatementsMode: boolean): TabListPage => ({
  page: 1,
  searchTerm: '',
  sortBy: defaultTabSortBy(isStatementsMode),
});

/**
 * Statements API params for one page of a royalty tab's table. Built in one
 * place for `StatementsDataTable` and Client Summary's tab check, so the check
 * for a tab's first page is the same request (and React Query cache entry) as
 * the table opening on it.
 */
export const tabStatementsParams = (
  { clientId, serialTitleId, serviceTypes, titleId, workspaceId }: TabListScope,
  { page, searchTerm, sortBy }: TabListPage,
): StatementsQueryParams => ({
  beneficiaryClientId: clientId,
  // A series' royalty statements are filed against its seasons, so the series
  // id lands on `beneficiarySerialTitleId`, not `beneficiaryTitleId` (APP-2431).
  ...(serialTitleId
    ? { beneficiarySerialTitleId: serialTitleId }
    : { beneficiaryTitleId: titleId }),
  beneficiaryWorkspaceId: workspaceId,
  page,
  searchTerm,
  serviceType: serviceTypes,
  sortBy: ensureStableSort(sortBy),
  status: LISTED_STATEMENT_STATUS,
});

/**
 * Invoices API params for one page of a Disbursements/Residuals tab's table.
 * See {@link tabStatementsParams} for why it's shared. Built on
 * {@link paidInvoicesParams}, which the sidebar tables and CSV exports also
 * use (APP-2510), so the paid status and stable sort stay in one place.
 */
export const tabInvoicesParams = (
  { clientId, serviceTypes, titleId, workspaceId }: TabListScope,
  { page, searchTerm, sortBy }: TabListPage,
): InvoicesQueryParams =>
  paidInvoicesParams({
    beneficiaryClientId: clientId,
    beneficiaryTitleId: titleId,
    beneficiaryWorkspaceId: workspaceId,
    page,
    searchTerm,
    serviceType: serviceTypes,
    sortBy,
  });
